import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import CargomaticCard from 'components/v2/card';
import AuditHistory from 'components/modules/shipments/AuditHistory';
import EditReferenceNumbersModal from 'components/modules/shipments/EditReferenceNumbersModal';
import DataPoller from 'components/ui/DataPoller';
import Alert from 'components/ui/tailwind/Alert';
import ArrivedModal from 'components/ui/tailwind/modals/ArrivedModal';
import PortIntegration from 'components/modules/equipment/PortIntegration';
import OrderShipmentsStore from 'stores/OrderShipmentsStore';
import { withTranslation } from 'react-i18next';
import OrdersActionCreators from '../../../actions/OrdersActionCreators';
import OrdersStore from '../../../stores/OrdersStore';
import SessionStore from '../../../stores/SessionStore';
import StatusBar from './StatusBar';
import OrderMap from './OrderMap';
import { OrderDocuments } from './OrderDocuments';
import CommentList from '../comments/CommentList';
import InboundDetails from './OrderDetails';
import ReferenceNumbers from './ReferenceNumbers';
import ShipmentsList from './ShipmentsList';
import EquipmentsList from './EquipmentsList';
import AccountsPayable from './AccountsPayable';
import AccountsReceivable from './AccountsReceivable';
import AccessorialsList from './AccessorialsList';
import BulkAddAccessorialsModal from '../shipments/accounting/BulkAddAccessorialsModal';

import { isDrayageShipment } from '../../../helpers/shipment-types';

const POLLING_FREQ = 30000;

const shipmentQueryOptions = {
  paginate: true,
  per_page: 15,
  page: 1,
};

class Module extends Component {
  constructor(props) {
    super(props);

    const hasFinanceRole = SessionStore.hasFinanceRole();
    this.state = {
      order: null,
      title: '',
      shipmentsWithZeroValueAccessorials: [],
      audits: [],
      showEditReferenceNumbersModal: false,
      showArrivedModal: false,
      showBulkAddAccessorialsModal: false,
      hasFinanceRole,
      stagingShipments: [],
      bookedShipments: [],
      shipmentListPagination: {},
    };
  }

  componentDidMount() {
    OrdersActionCreators.getOrderShipments(
      this.props.id || this.props.match.params.id,
      shipmentQueryOptions,
    );
    OrderShipmentsStore.addChangeListener(this.handleOrderShipmentsStoreChange);
    OrdersStore.addStagingShipmentsChangeListener(
      this.handleStagingShipmentsChange,
    );
    OrdersStore.addChangeListener(this.handleOrdersStoreChange);
    SessionStore.addChangeListener(this.handleSessionStoreChange);
    this.handleStagingShipmentsChange();
  }

  componentWillUnmount() {
    OrdersStore.removeChangeListener(this.handleOrdersStoreChange);
    SessionStore.removeChangeListener(this.handleSessionStoreChange);
    OrdersActionCreators.resetStagingShipments();
    OrdersActionCreators.resetOrderShipments();
  }

  getOrder() {
    const { order } = this.state;
    return order || {};
  }

  getShipments() {
    return this.state.bookedShipments || [];
  }

  getShipmentsWithZeroValueAccessorials(order) {
    if (!order) {
      return [];
    }
    const orderShipments = this.state.bookedShipments || [];
    return orderShipments.filter((job) => {
      const receivables = job.receivables || [];
      const payables = job.payables || [];

      const zeroCostAR = receivables.filter((s) => s.value === 0);
      const zeroCostAP = payables.filter((s) => s.value === 0);
      return zeroCostAR.length > 0 || zeroCostAP > 0;
    });
  }

  handleOrderShipmentsStoreChange = () => {
    const orderShipments = OrderShipmentsStore.getOrderShipments();
    this.updateShipmentsState(orderShipments);
  };

  handleOrdersStoreChange = () => {
    const order = OrdersStore.getOrder();
    const audits = OrdersStore.getAudits();
    if (!this.state.title && order && order.order_id) {
      document.title = order.order_id;
      this.setState({ title: order.order_id });
    }

    const shipmentsWithZeroValueAccessorials =
      this.getShipmentsWithZeroValueAccessorials(order);
    this.setState({
      order,
      shipmentsWithZeroValueAccessorials,
      hasZeroValueAccessorials: shipmentsWithZeroValueAccessorials.length > 0,
      audits,
    });
  };

  handleStagingShipmentsChange = () => {
    const stagingShipments = OrdersStore.getStagingShipments();
    if (!stagingShipments) {
      return;
    }
    this.setState({ stagingShipments });
  };

  handleCommentAdd = (comment) => {
    const {
      order: { _id: orderId },
    } = this.state;

    OrdersActionCreators.addComment(orderId, comment);
  };

  handleAddBulkAccessorialsClick = () =>
    this.setState({ showBulkAddAccessorialsModal: true });

  handleSessionStoreChange = () => {
    const hasFinanceRole = SessionStore.hasFinanceRole();
    this.setState({
      hasFinanceRole,
    });
  };

  updateReferenceNumbers(references) {
    OrdersActionCreators.updateOrder(this.state.order._id, {
      reference_numbers: references,
    });
    this.setState({
      showEditReferenceNumbersModal: false,
    });
  }

  renderMissingDocumentation() {
    const drayageShipmentsMissingDocumentation = this.getShipments().filter(
      (s) => isDrayageShipment(s) && s.missing_docs,
    );

    if (
      this.state.order &&
      !this.state.order.missing_docs &&
      drayageShipmentsMissingDocumentation.length === 0
    )
      return null;
    const { t } = this.props;
    return (
      <Alert
        dataTestId="Alert-564a51e7"
        status="red"
        message={t('MISSING_DOC_FOR_ORDER')} />
    );
  }

  renderOutstandingARStatus() {
    const {
      order: { outstanding_ar, outstanding_ap },
    } = this.state;
    if (!outstanding_ar && !outstanding_ap) {
      return null;
    }
    const { t } = this.props;
    return (
      (<Alert
        dataTestId="Alert-29539371"
        status="yellow"
        message={t('OUTSTANDING_BALANCE_FOR_ORDER')} />)
    );
  }

  renderZeroBalanceNotification() {
    if (!this.state.hasZeroValueAccessorials) {
      return null;
    }

    const { shipmentsWithZeroValueAccessorials: jobs } = this.state;
    const { t } = this.props;
    return (
      (<Alert
        dataTestId="Alert-8cebe271"
        status="yellow"
        message={t('NO_ACCESSORIAL_RATE_OR_COST_FOR_SHIPMENT')}>
        <ul data-testid="ul-ac3107c2">
          {jobs.map((job, index) => {
            const jobLink = `/shipments/${job._id}`;
            return (
              (<li data-testid={`li-e2b97ba0-${index}`} className="mb-2" key={job._id}>
                <a data-testid={`a-90bccd1b-${index}`} href={jobLink}>{job.shipment_id}</a>
              </li>)
            );
          })}
        </ul>
      </Alert>)
    );
  }

  getShipmentsEligibleForAccessorialsManagement() {
    return this.getShipments().filter((s) => {
      return !_.isEmpty(s.carrier);
    });
  }

  updateShipmentsState(bookedShipments) {
    this.setState((prevState) => {
      const { stagingShipments, shipmentListPagination: currentPagination } =
        prevState;

      const shipments = [...stagingShipments, ...bookedShipments];

      const pagination = OrderShipmentsStore.getOrderShipmentPaginationData();
      const shipmentListPagination = {
        totalPages: pagination.pages,
        currentPage: currentPagination.currentPage || 1,
      };
      return {
        bookedShipments,
        shipments,
        shipmentListPagination,
      };
    });
  }

  onGetOrderShipments = () => {
    const orderId = this.props.id || this.props.match.params.id;
    const options = {
      ...shipmentQueryOptions,
      page: this.state.shipmentListPagination.currentPage || 1,
    };
    OrdersActionCreators.getOrderShipments(orderId, options);
  };

  handleShipmentListPagination = (eventKey) => {
    const paginationData = OrderShipmentsStore.getOrderShipmentPaginationData();
    this.setState(
      {
        shipmentListPagination: {
          currentPage: eventKey,
          totalPages: paginationData.pages,
        },
      },
      this.onGetOrderShipments,
    );
  };

  render() {
    const { order, bookedShipments, stagingShipments } = this.state;
    const id = this.props.id || this.props.match.params.id;
    const { hideMap, renderedInModal, t } = this.props;

    return (
      <div data-testid="div-95433961" className="m-0 mx-auto my-8 max-w-screen-2xl">
        <DataPoller
          dataTestId="DataPoller-143d43d3"
          action={() => OrdersActionCreators.getOrder(id)}
          frequency={POLLING_FREQ} />
        <DataPoller
          dataTestId="DataPoller-cd654e78"
          action={() => OrdersActionCreators.getOrderAudits(id)}
          frequency={POLLING_FREQ} />
        <DataPoller
          dataTestId="DataPoller-af62a7e5"
          action={this.onGetOrderShipments}
          frequency={POLLING_FREQ} />
        {order && order._id && (
          <div data-testid="div-889d6180" id="order-details-container">
            {this.renderMissingDocumentation()}
            {this.renderOutstandingARStatus()}
            {this.renderZeroBalanceNotification()}
            <CargomaticCard
              dataTestId="CargomaticCard-29de49d5"
              className=""
              removeHorizontalPadding
              removeVerticalPadding>
              <StatusBar
                order={order}
                shipments={bookedShipments}
                onArrivedClick={() => this.setState({ showArrivedModal: true })}
              />
              {!hideMap && (
                <div data-testid="div-9c2d5406" className="my-2">
                  <OrderMap
                    arriving={order.arriving}
                    delivering={order.delivering}
                  />
                </div>
              )}
            </CargomaticCard>
            <div data-testid="div-f11b63c0" className="mt-5 grid sm:grid-cols-12 sm:gap-6">
              <div data-testid="div-9f2bf684" className="sm:col-span-4">
                <InboundDetails
                  order={order}
                  onEdit={(edit) => {
                    OrdersActionCreators.updateOrder(order._id, edit);
                  }}
                />
              </div>
              <div data-testid="div-f6319b88" className="sm:col-span-4">
                <ReferenceNumbers
                  order={order}
                  onEdit={() => {
                    this.setState({ showEditReferenceNumbersModal: true });
                  }}
                />
              </div>
              <div data-testid="div-d2d86e6e" className="sm:col-span-4 space-y-6">
                <OrderDocuments order={order} shipments={bookedShipments} />
                <CargomaticCard
                  dataTestId="CargomaticCard-e64798ff"
                  header={() => (
                    <h3 data-testid="h3-67608da1" className="my-0">{t('NOTE', { count: 2 })}</h3>
                  )}>
                  <CommentList
                    comments={order.comments}
                    onCommentAdd={this.handleCommentAdd}
                  />
                </CargomaticCard>
              </div>
            </div>
            <div data-testid="div-a7e55759" className="my-6">
              <EquipmentsList order={order} shipments={bookedShipments} />
            </div>
            {/* Temp hide due to CARGO-9056 */}
            {/* {SessionStore.isAdmin() && order.order_type !== 'export' && ( */}
            {/*  <div className="my-6"> */}
            {/*    <PortIntegration isShipment={false} data={order} /> */}
            {/*  </div> */}
            {/* )} */}
            <ShipmentsList
              order={order}
              bookedShipments={bookedShipments}
              stagingShipments={stagingShipments}
              renderedInModal={renderedInModal}
              onGetPaginatedShipments={this.handleShipmentListPagination}
              paginationData={this.state.shipmentListPagination}
            />
            <div
              data-testid="div-99fac929"
              className="my-6 grid sm:grid-cols-6 sm:gap-x-4 sm:gap-y-6">
              <div data-testid="div-da1c08a7" className="sm:col-span-4">
                <div
                  data-testid="div-89a19554"
                  className="grid sm:grid-cols-2 sm:gap-x-4 sm:gap-y-6">
                  <div data-testid="div-1067acea" className="sm:col-span-1">
                    <AccountsReceivable
                      order={order}
                      shipments={bookedShipments}
                    />
                  </div>
                  <div data-testid="div-d143ceed" className="sm:col-span-1">
                    <AccountsPayable
                      order={order}
                      shipments={bookedShipments}
                    />
                  </div>
                </div>
                {this.state.hasFinanceRole && (
                  <div data-testid="div-c12fb1a8" className="my-6">
                    <AccessorialsList
                      order={order}
                      shipments={bookedShipments}
                      onAddBulkAccessorials={
                        this.handleAddBulkAccessorialsClick
                      }
                    />
                  </div>
                )}
              </div>
              <CargomaticCard
                dataTestId="CargomaticCard-cc3f4c78"
                className="sm:col-span-2"
                header={() => <h3 data-testid="h3-a3aa483b" className="my-0">{t('AUDIT_HISTORY')}</h3>}>
                <AuditHistory audits={this.state.audits} />
              </CargomaticCard>
            </div>
          </div>
        )}
        {order?.reference_numbers && (
          <EditReferenceNumbersModal
            reference_numbers={order.reference_numbers}
            shipper={order.shipper}
            isIntermodalShipmentType
            page="Orders"
            show={this.state.showEditReferenceNumbersModal}
            onSave={(references) => this.updateReferenceNumbers(references)}
            onClose={() => {
              this.setState({ showEditReferenceNumbersModal: false });
            }}
          />
        )}
        {this.state.showArrivedModal && (
          <ArrivedModal
            dataTestId="ArrivedModal-c00242a0"
            show={this.state.showArrivedModal}
            onComplete={(workflow, meta) => {
              OrdersActionCreators.transitionOrder(order, workflow, meta);
            }}
            onHide={() => this.setState({ showArrivedModal: false })} />
        )}
        {this.state.showBulkAddAccessorialsModal && (
          <BulkAddAccessorialsModal
            show={this.state.showBulkAddAccessorialsModal}
            orderId={this.getOrder()._id}
            shipmentIds={(this.getShipmentsEligibleForAccessorialsManagement()).map(s => s._id)}
            onSave={this.handleBulkCreateAccessorials}
            onHide={() => {
              this.setState({ showBulkAddAccessorialsModal: false });
            }}
            edit={false}
          />
        )}
        <div data-testid="div-1d32f7ce" style={{ height: 400 }}>&nbsp;</div>
      </div>
    );
  }
}

Module.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  renderedInModal: PropTypes.bool,
};

export default withTranslation()(Module);
